import React, { useEffect, useState } from "react";
import { api, API, fmtDate, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Download, FileJson, ArrowDownCircle, ArrowUpCircle, Wallet } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { inDateRange } from "@/components/crm/Filters";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const MODES = ["UPI","Cheque","Cash","RTGS","NEFT","Card","Bank Transfer"];
const CATEGORIES_IN = ["Advance","Balance","Full Payment","Subsidy Received","Refund"];
const CATEGORIES_OUT = ["Material Purchase","Logistics","Salary","Rent","Utilities","Travel","Marketing","Vendor Payment","Other"];

const empty = {
  direction: "IN", amount: 0, date: new Date().toISOString().slice(0,10),
  mode: "UPI", reference: "", party_name: "", customer_id: "", vendor_name: "",
  tax_invoice_id: "", category: "", notes: "",
};

export default function Payments() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total_in: 0, total_out: 0, net: 0, count: 0 });
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [dir, setDir] = useState("ALL");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  const load = async () => {
    const params = new URLSearchParams();
    if (dir !== "ALL") params.set("direction", dir);
    if (fFrom) params.set("from_date", fFrom);
    if (fTo) params.set("to_date", fTo);
    setItems((await api.get(`/payments?${params.toString()}`)).data);
    try { setSummary((await api.get("/payments/summary")).data); } catch { setSummary({ total_in:0,total_out:0,net:0,count:0 }); }
    try { setCustomers((await api.get("/customers")).data); } catch { setCustomers([]); }
    try { setInvoices((await api.get("/tax-invoices")).data); } catch { setInvoices([]); }
  };
  useEffect(() => { load(); }, [dir, fFrom, fTo]);

  const save = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (!payload.customer_id) delete payload.customer_id;
      if (!payload.tax_invoice_id) delete payload.tax_invoice_id;
      if (editId) await api.patch(`/payments/${editId}`, payload);
      else await api.post("/payments", payload);
      toast.success(editId ? "Payment updated" : "Payment saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (p) => { setForm({ ...empty, ...p, customer_id: p.customer_id || "", tax_invoice_id: p.tax_invoice_id || "" }); setEditId(p.id); setOpen(true); };
  const del = async (p) => { try { await api.delete(`/payments/${p.id}`); toast.success("Deleted"); load(); } catch(e){ toast.error(formatError(e.response?.data?.detail)); } };

  const download = (fmt) => {
    const url = `${API}/payments/export.${fmt}?auth=${token}`;
    // File endpoint requires bearer; use fetch + blob download
    api.get(`/payments/export.${fmt}`, { responseType: "blob" }).then(r => {
      const blob = new Blob([r.data], { type: fmt === "csv" ? "text/csv" : "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `payments-${new Date().toISOString().slice(0,10)}.${fmt}`;
      link.click();
    }).catch(e => toast.error(formatError(e.response?.data?.detail) || "Download failed"));
  };

  const visible = items.filter(p => matchesQuery(p, search));

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Payments <span className="text-slate-400 text-lg font-medium">/ भुगतान (In / Out)</span></h1>
          <p className="text-slate-500 text-sm">Customer receipts & vendor payments — with CA-ready CSV/JSON export.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search party, ref..." testid="payments-search-input"/>
          {user?.role === "admin" && (
            <>
              <Button variant="outline" onClick={()=>download("csv")} data-testid="export-payments-csv"><Download className="w-4 h-4 mr-1"/>CSV (CA)</Button>
              <Button variant="outline" onClick={()=>download("json")} data-testid="export-payments-json"><FileJson className="w-4 h-4 mr-1"/>JSON</Button>
            </>
          )}
          <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v){ setForm(empty); setEditId(null);} }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-payment-btn"><Plus className="w-4 h-4 mr-1"/>New Payment</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Payment</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Direction*</Label>
                  <Select value={form.direction} onValueChange={v=>setForm({...form, direction:v, category:""})}>
                    <SelectTrigger data-testid="payment-direction"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IN">IN — Customer / Subsidy Received</SelectItem>
                      <SelectItem value="OUT">OUT — Vendor / Expense Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Amount (₹)*</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} data-testid="payment-amount"/></div>
                <div><Label className="text-xs font-semibold text-slate-600">Date*</Label>
                  <Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Mode</Label>
                  <Select value={form.mode} onValueChange={v=>setForm({...form, mode:v})}>
                    <SelectTrigger data-testid="payment-mode"><SelectValue/></SelectTrigger>
                    <SelectContent>{MODES.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Category</Label>
                  <Select value={form.category||""} onValueChange={v=>setForm({...form, category:v})}>
                    <SelectTrigger data-testid="payment-category"><SelectValue placeholder="Category"/></SelectTrigger>
                    <SelectContent>{(form.direction==="IN"?CATEGORIES_IN:CATEGORIES_OUT).map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs font-semibold text-slate-600">Reference (UTR / Chq No.)</Label>
                  <Input value={form.reference||""} onChange={e=>setForm({...form, reference:e.target.value})} data-testid="payment-reference"/></div>

                {form.direction === "IN" ? (
                  <>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Customer</Label>
                      <Select value={form.customer_id||""} onValueChange={v=>setForm({...form, customer_id:v})}>
                        <SelectTrigger data-testid="payment-customer"><SelectValue placeholder="Pick customer"/></SelectTrigger>
                        <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Linked Tax Invoice (optional)</Label>
                      <Select value={form.tax_invoice_id||""} onValueChange={v=>setForm({...form, tax_invoice_id:v})}>
                        <SelectTrigger data-testid="payment-invoice"><SelectValue placeholder="Link an invoice"/></SelectTrigger>
                        <SelectContent>{invoices.map(t=><SelectItem key={t.id} value={t.id}>{t.invoice_no} · {t.customer_name} · {fmtINR(t.grand_total)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Vendor / Party Name*</Label>
                    <Input value={form.vendor_name||form.party_name||""} onChange={e=>setForm({...form, vendor_name:e.target.value, party_name:e.target.value})} placeholder="Solarium Green / Waaree / etc." data-testid="payment-vendor"/></div>
                )}

                <div className="sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Notes</Label>
                  <Textarea rows={2} value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})} data-testid="payment-notes"/></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.amount || !form.date} data-testid="save-payment-btn">{editId ? "Update" : "Save"} Payment</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><ArrowDownCircle className="w-5 h-5"/></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Total IN</div>
            <div className="text-lg font-black font-mono text-emerald-700" data-testid="summary-in">{fmtINR(summary.total_in)}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center"><ArrowUpCircle className="w-5 h-5"/></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Total OUT</div>
            <div className="text-lg font-black font-mono text-rose-700" data-testid="summary-out">{fmtINR(summary.total_out)}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Wallet className="w-5 h-5"/></div>
          <div><div className="text-[10px] uppercase text-slate-500 font-semibold">Net Balance</div>
            <div className={`text-lg font-black font-mono ${summary.net>=0?"text-emerald-700":"text-rose-700"}`} data-testid="summary-net">{fmtINR(summary.net)}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-[10px] uppercase text-slate-500 font-semibold">Entries</div>
          <div className="text-2xl font-black text-slate-900">{summary.count}</div>
          <div className="text-[10px] text-slate-500">(all-time)</div>
        </CardContent></Card>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} onClear={()=>{setFFrom("");setFTo("");setSearch("");setDir("ALL");}}
        extra={<Select value={dir} onValueChange={setDir}>
          <SelectTrigger className="w-40 h-9" data-testid="filter-direction"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All</SelectItem><SelectItem value="IN">IN only</SelectItem><SelectItem value="OUT">OUT only</SelectItem></SelectContent>
        </Select>}
      />

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>#</TableHead><TableHead>Date</TableHead><TableHead>Direction</TableHead>
            <TableHead>Party</TableHead><TableHead>Mode</TableHead>
            <TableHead>Category</TableHead><TableHead>Ref</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400">{search?`No matches`:"No payments yet."}</TableCell></TableRow>}
            {visible.map(p=>(
              <TableRow key={p.id} data-testid={`payment-row-${p.payment_no}`}>
                <TableCell className="font-mono text-xs font-semibold">{p.payment_no}</TableCell>
                <TableCell className="text-xs">{fmtDate(p.date)}</TableCell>
                <TableCell>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${p.direction==="IN"?"bg-emerald-100 text-emerald-800":"bg-rose-100 text-rose-800"}`}>{p.direction}</span>
                </TableCell>
                <TableCell className="font-medium text-sm">{p.party_name||"-"}</TableCell>
                <TableCell className="text-xs">{p.mode}</TableCell>
                <TableCell className="text-xs">{p.category||"-"}</TableCell>
                <TableCell className="text-xs font-mono">{p.reference||"-"}</TableCell>
                <TableCell className={`text-right font-mono font-bold ${p.direction==="IN"?"text-emerald-700":"text-rose-700"}`}>{fmtINR(p.amount)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={()=>edit(p)} data-testid={`edit-payment-${p.payment_no}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    {user?.role === "admin" && <DeleteButton title={`Delete payment ${p.payment_no}?`} description="This cannot be undone." onConfirm={()=>del(p)} testid={`delete-payment-${p.payment_no}`}/>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
